import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { TimePeriod } from '@/types/dashboard'

export interface UserStats {
  totalUniqueUsers: number
  activeUsers24h: number
  activeUsers7d: number
  activeUsers30d: number
  newUsers24h: number
  newUsers7d: number
  newUsers30d: number
  // New-users count for the currently selected period (matches `useUserStats(period)`).
  // Equals `totalUniqueUsers` when period === 'all'.
  periodNewUsers: number
  // New-users count for the preceding equivalent window, used for growth-rate comparison.
  // Null when period === 'all' (no comparison window).
  previousPeriodNewUsers: number | null
  dauMauRatio: number
  creatorRetentionRate: number
  totalComments: number
  totalLikes: number
  reportedCommentsCount: number
}

// Shape returned by the public.get_dashboard_user_stats() Postgres RPC.
// See dextoro-admin/supabase/migrations/20260409000002_dashboard_user_stats_rpc.sql.
interface DashboardUserStatsRpc {
  total_unique_users: number
  active_users_24h: number
  active_users_7d: number
  active_users_30d: number
  new_users_24h: number
  new_users_7d: number
  new_users_30d: number
  prev_new_users_24h: number
  prev_new_users_7d: number
  prev_new_users_30d: number
}

export function useUserStats(period: TimePeriod = '7d') {
  return useQuery({
    queryKey: ['user-community-stats', period],
    queryFn: async (): Promise<UserStats> => {
      // User session aggregation happens server side via RPC to avoid
      // PostgREST's 1000-row cap on unranged .select() calls. See the
      // migration header for the full context.
      const [userStatsResult, commentsResult, likesResult, reportedResult, creatorsResult] = await Promise.all([
        supabase.rpc('get_dashboard_user_stats'),

        supabase
          .from('token_comments')
          .select('*', { count: 'exact', head: true })
          .eq('is_deleted', false),

        supabase
          .from('token_likes')
          .select('*', { count: 'exact', head: true }),

        supabase
          .from('comment_reports')
          .select('*', { count: 'exact', head: true }),

        // Creator retention (creators with 2+ tokens).
        // TODO(dashboard-truncation): this query is unranged and will start
        // truncating once launched_tokens exceeds 1000 rows. Migrate to an
        // RPC in a follow-up PR.
        supabase
          .from('launched_tokens')
          .select('creator_address'),
      ])

      const rpc = (userStatsResult.data ?? {}) as Partial<DashboardUserStatsRpc>
      const totalUniqueUsers = rpc.total_unique_users ?? 0
      const activeUsers24h = rpc.active_users_24h ?? 0
      const activeUsers7d = rpc.active_users_7d ?? 0
      const activeUsers30d = rpc.active_users_30d ?? 0
      const newUsers24h = rpc.new_users_24h ?? 0
      const newUsers7d = rpc.new_users_7d ?? 0
      const newUsers30d = rpc.new_users_30d ?? 0

      // Map the RPC's period-specific counts onto the caller's selected period.
      let periodNewUsers: number
      let previousPeriodNewUsers: number | null
      switch (period) {
        case '24h':
          periodNewUsers = newUsers24h
          previousPeriodNewUsers = rpc.prev_new_users_24h ?? 0
          break
        case '7d':
          periodNewUsers = newUsers7d
          previousPeriodNewUsers = rpc.prev_new_users_7d ?? 0
          break
        case '30d':
          periodNewUsers = newUsers30d
          previousPeriodNewUsers = rpc.prev_new_users_30d ?? 0
          break
        case 'all':
          periodNewUsers = totalUniqueUsers
          previousPeriodNewUsers = null
          break
      }

      const dauMauRatio = activeUsers30d > 0
        ? (activeUsers24h / activeUsers30d) * 100
        : 0

      // Creator retention: creators with 2+ tokens
      const creatorTokenCounts = new Map<string, number>()
      creatorsResult.data?.forEach((t: { creator_address: string }) => {
        if (t.creator_address) {
          creatorTokenCounts.set(t.creator_address, (creatorTokenCounts.get(t.creator_address) || 0) + 1)
        }
      })
      const totalCreators = creatorTokenCounts.size
      const retainedCreators = [...creatorTokenCounts.values()].filter((c) => c >= 2).length
      const creatorRetentionRate = totalCreators > 0
        ? (retainedCreators / totalCreators) * 100
        : 0

      // Reported comments: graceful fallback
      let reportedCommentsCount = 0
      if (!reportedResult.error && reportedResult.count != null) {
        reportedCommentsCount = reportedResult.count
      }

      return {
        totalUniqueUsers,
        activeUsers24h,
        activeUsers7d,
        activeUsers30d,
        newUsers24h,
        newUsers7d,
        newUsers30d,
        periodNewUsers,
        previousPeriodNewUsers,
        dauMauRatio,
        creatorRetentionRate,
        totalComments: commentsResult.count || 0,
        totalLikes: likesResult.count || 0,
        reportedCommentsCount,
      }
    },
    refetchInterval: 30000,
  })
}
